/**
 * The Cloud (Azure) board.
 *
 * Everything here is read live from Azure Resource Manager by the NOVA backend
 * — Resource Graph for the estate and workload status, Resource Health for
 * Azure's own verdicts, Advisor for its recommendations, and Azure Monitor for
 * NOVA's own metrics. Nothing is seeded, sampled, or simulated.
 *
 * The board has exactly two honest states: it is showing what Azure reported,
 * or it is explaining why it cannot. The single most important number on the
 * page is "subscriptions visible" — Resource Graph only ever returns what the
 * credential can read, so a partial answer must never render as a smaller,
 * healthier estate.
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, Freshness, PageHeader, Skeleton } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { FilterRow, useBoardRange } from "@/components/board";
import { NAV_SECTIONS } from "@/components/nav-sections";
import {
  useCloudAdvisor,
  useCloudAlerts,
  useCloudChanges,
  useCloudCost,
  useCloudGovernance,
  useCloudOverview,
  useCloudServiceHealth,
  useCloudWorkloads,
} from "@/lib/api";
import { parseListParam } from "@/lib/drill";
import { logger } from "@/lib/logger";

import { AdvisorPanel } from "./advisor-panel";
import { AlertInventory } from "./alert-inventory";
import { AlertsSection } from "./alerts-section";
import { ChangesSection } from "./changes-section";
import { CostAnomalyPanel } from "./cost-anomaly-panel";
import { CostSection } from "./cost-section";
import { EstateGrowthPanel } from "./estate-growth-panel";
import { EstateSummary } from "./estate-summary";
import { GovernancePanel } from "./governance-panel";
import { CloudKpiStrip } from "./kpi-strip";
import { CloudSetupPanel } from "./not-configured";
import { NovaPanel } from "./nova-panel";
import { SUB_PARAM } from "./params";
import { ResourceCensus } from "./resource-census";
import { ResourceHealth } from "./resource-health-panel";
import { ServiceHealthBanner } from "./service-health-banner";
import { SubscriptionGrid } from "./subscription-grid";
import { WorkloadTable } from "./workload-table";

const SECTION = NAV_SECTIONS.find((section) => section.label === "Cloud");

export { CloudAlertsPage } from "./alerts-page";
export { CloudChangesPage } from "./changes-page";
export { CLOUD_ALERTS_PATH, CLOUD_CHANGES_PATH } from "./params";

export function CloudPage() {
  const queryClient = useQueryClient();
  const range = useBoardRange();
  const subIds = parseListParam(useSearchParams()[0].get(SUB_PARAM));
  const overview = useCloudOverview();
  const workloads = useCloudWorkloads();
  const serviceHealth = useCloudServiceHealth();
  const advisor = useCloudAdvisor();
  const alerts = useCloudAlerts();
  const changes = useCloudChanges();
  const cost = useCloudCost();
  const governance = useCloudGovernance();

  const data = overview.data;
  const refreshing =
    overview.isFetching ||
    workloads.isFetching ||
    serviceHealth.isFetching ||
    advisor.isFetching ||
    alerts.isFetching ||
    changes.isFetching ||
    cost.isFetching ||
    governance.isFetching;

  // The backend rebuilds its snapshot on its own TTL; flag the data as stale
  // once it is meaningfully older than one rebuild window, not the instant it
  // ages past it.
  const staleAfterMs = data ? data.cache_ttl_sec * 1000 * 2 : undefined;

  const refresh = () => {
    logger.debug("[cloud] manual refresh requested");
    void queryClient.invalidateQueries({ queryKey: ["cloud"] });
  };

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Cloud"
        subtitle={
          SECTION?.description ??
          "Azure subscriptions and hosted workloads, read live from Azure Resource Manager."
        }
        actions={
          <div className="flex items-center gap-4">
            {data?.collected_at && (
              <Freshness
                label="Azure read"
                since={data.collected_at}
                staleAfter={staleAfterMs}
                pulse={refreshing}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {overview.error && (
        <Alert tone="danger" title="Couldn't load the Cloud board">
          {overview.error instanceof Error
            ? overview.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one."}
        </Alert>
      )}

      {overview.isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <Skeleton height="6rem" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="24rem" />
        </div>
      ) : (
        data && (
          <>
            {!data.configured ? (
              <CloudSetupPanel
                statusDetail={data.status_detail}
                expectedSubscriptions={data.summary.subscriptions_expected}
              />
            ) : (
              <>
                {data.errors.length > 0 && (
                  <Alert tone="warning" title="This snapshot is incomplete">
                    <ul className="flex flex-col gap-1">
                      {data.errors.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {serviceHealth.data && <ServiceHealthBanner health={serviceHealth.data} />}

                {/* L0 strip: the six numbers that answer "is anything wrong",
                    each linking into the section that explains it. */}
                <CloudKpiStrip summary={data.summary} />

                {/* One filter row, scoping every section below it (§4.3). */}
                <FilterRow
                  dimensions={[
                    {
                      param: SUB_PARAM,
                      label: "Subscription",
                      options: data.subscriptions.map((subscription) => ({
                        value: subscription.subscription_id,
                        label: subscription.name,
                      })),
                    },
                  ]}
                />

                {alerts.data && (
                  <AlertsSection alerts={alerts.data} subIds={subIds} range={range} />
                )}

                {cost.data && <CostSection cost={cost.data} subIds={subIds} range={range} />}

                {cost.data?.configured && <CostAnomalyPanel cost={cost.data} />}

                {changes.data && (
                  <ChangesSection changes={changes.data} subIds={subIds} range={range} />
                )}

                <EstateSummary summary={data.summary} workloads={data.workload_summary} />

                {/* The daily `estate_day` rollups, read back — growth the live
                    census cannot show on its own. */}
                <EstateGrowthPanel growth={data.estate_growth} />

                {workloads.data && (
                  <WorkloadTable
                    workloads={workloads.data.workloads}
                    unreachable={workloads.data.unreachable_subscriptions}
                  />
                )}

                <SubscriptionGrid subscriptions={data.subscriptions} />

                {governance.data && <GovernancePanel governance={governance.data} />}

                {data.nova && <NovaPanel footprint={data.nova} />}

                {data.resource_health && <ResourceHealth panel={data.resource_health} />}

                {advisor.data && <AdvisorPanel advisor={advisor.data} />}

                <AlertInventory alertRules={data.alert_rules} subscriptions={data.subscriptions} />

                <ResourceCensus
                  resourceTypes={data.resource_types}
                  totalTypes={data.summary.resource_types}
                />
              </>
            )}
          </>
        )
      )}
    </main>
  );
}
